import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";

import type { NamedRow } from "@/lib/namedParts";
import { Button } from "@/ui/Button";
import { IconButton } from "@/ui/IconButton";
import { Tooltip } from "@/ui/Tooltip";

/**
 * The canonical list of an asset's named documents (#492).
 *
 * One row per named document family, each opening its own editor. This is what
 * replaces the generic parts list for the two families this ticket owns, and the
 * difference that matters is what is NOT here: no "Part key" free-text input, no
 * role select, no language-prefix control, no required-role coverage summary.
 * A row is a document with a product name, and the name is READ-ONLY.
 *
 * WHY THE LABEL IS READ-ONLY, where the canonical reference has an editable
 * "Describe this row" field. A row's label IS its role, and the role is the
 * service's own vocabulary — `library.py`'s `_PART_MANIFEST` decides which roles
 * a type may carry, and `validate_parts` refuses anything else. An editable label
 * would therefore be a part-key input wearing a friendlier word, which is exactly
 * the manual-key escape hatch the 2026-08-25 amendment forbids. Storing the label
 * anywhere else would mean inventing a per-row field inside the `steps` document,
 * whose schema no authority in this repository publishes.
 *
 * REORDER IS BUTTONS, and stays buttons. `parts` is an ordered list by contract,
 * so moving a row is an edit of the same weight as retyping one; two buttons are
 * operable by keyboard, pointer and assistive technology on the first day, which
 * is #455's requirement and `PartsEditor`'s existing reasoning.
 */

export function NamedRowList({
  rows,
  readOnly,
  onOpen,
  onMove,
  onRemove,
  add,
}: {
  rows: readonly NamedRow[];
  readOnly: boolean;
  onOpen: (role: string) => void;
  /**
   * Reorder and remove, or ABSENT where the family has neither.
   *
   * Optional because `background_profile` (#914) has genuinely neither: all
   * three of its roles are required of every version, so a removal could never
   * be published, and its three files are opened by fixed names, so their order
   * in the parts array reaches no runtime decision. Passing no-op handlers
   * beside `readOnly` would have said the controls exist and are merely
   * withheld, which is not true of that family.
   */
  onMove?: (role: string, to: number) => void;
  onRemove?: (role: string) => void;
  /**
   * The add control, when this list owns it — OPTIONAL, because the canonical
   * reference puts it in different places on different screens.
   *
   * The Main-concern detail's "Add prompt" is a header action beside the
   * heading; a list that rendered its own as well would put two identically
   * named buttons on one screen, which is a duplicate control rather than a
   * convenience. The domain Steps editor puts its "Add prompt" under the rows.
   * So the caller says which, and passing nothing means the list has none.
   */
  add?: {
    /** What the control says it will add, e.g. "Add prompt". */
    label: string;
    /** Why adding is refused, or `null`. Never an unexplained disabled button. */
    refusal: string | null;
    onAdd: () => void;
  };
}) {
  return (
    <div className="space-y-3">
      <ol
        aria-label="Documents, in authored order"
        className="overflow-hidden rounded-md border border-border bg-card"
      >
        {rows.map((row, index) => (
          <li
            key={row.role}
            className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2 last:border-b-0"
          >
            <span className="w-6 shrink-0 text-xs tabular-nums text-muted-foreground">
              {index + 1}.
            </span>

            <span className="min-w-0 flex-1 truncate text-sm text-foreground">
              {row.label}
              {row.documents.length > 1 ? (
                // A read-only fact, shown only when the asset actually carries
                // the document in more than one language and two rows would
                // otherwise be indistinguishable.
                <span className="ml-2 text-xs text-muted-foreground">
                  {row.documents
                    .map((document) => document.language ?? "all")
                    .join(" · ")}
                </span>
              ) : null}
            </span>

            <Button variant="ghost" size="sm" onClick={() => onOpen(row.role)}>
              Open
            </Button>

            {readOnly || (onMove === undefined && onRemove === undefined) ? null : (
              <div className="flex items-center gap-0.5">
                {onMove === undefined ? null : (
                  <>
                    <IconButton
                      label={`Move ${row.label} up`}
                      icon={<ArrowUp className="h-4 w-4" />}
                      disabled={index === 0}
                      onClick={() => onMove(row.role, index - 1)}
                    />
                    <IconButton
                      label={`Move ${row.label} down`}
                      icon={<ArrowDown className="h-4 w-4" />}
                      disabled={index === rows.length - 1}
                      onClick={() => onMove(row.role, index + 1)}
                    />
                  </>
                )}
                {onRemove === undefined ? null : (
                  <IconButton
                    label={`Remove ${row.label}`}
                    icon={<Trash2 className="h-4 w-4" />}
                    onClick={() => onRemove(row.role)}
                  />
                )}
              </div>
            )}
          </li>
        ))}
      </ol>

      {readOnly || add === undefined ? null : (
        // ONE control, never a role picker. What it adds is the next document
        // the SERVICE's manifest allows and this asset does not carry yet, and
        // the tooltip names it — so a clinician learns the document by its
        // product name rather than choosing from a vocabulary.
        <Tooltip content={add.refusal ?? add.label}>
          <Button
            variant="outline"
            size="sm"
            disabled={add.refusal !== null}
            onClick={add.onAdd}
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            {add.label}
          </Button>
        </Tooltip>
      )}
    </div>
  );
}
